import { DeleteFilled, EyeFilled, UploadOutlined } from "@ant-design/icons"
import { Button, Image, Skeleton, Space, Spin, Tooltip, Upload } from "antd"
import React, { useState } from "react"

import { validateFile } from "../../../../utils/file-validation.ts"
import type { ProjectListItem } from "../../types/types.ts"

const MAX_SIZE_MB = 10
const ALLOWED_FILE_EXTENSIONS = ["png", "jpeg", "jpg"]

interface ProjectCardPhotoProps {
  project: ProjectListItem
  photoEditable: boolean
  onUpdateMainPhoto: (
    projectId: string,
    file?: File | null
  ) => Promise<string | null>
}

export function ProjectCardPhoto({
  project,
  photoEditable,
  onUpdateMainPhoto
}: ProjectCardPhotoProps) {
  const [preview, setPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [href, setHref] = useState<string | null>(project?.main_photo || null)

  const src = href || ""

  const beforeUpload = async (file: File) => {
    const result = validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true
    })

    if (result !== true) {
      return false
    }

    try {
      setSaving(true)
      const newSrc = await onUpdateMainPhoto(project?.id, file)
      setHref(newSrc)
    } finally {
      setSaving(false)
    }
  }

  const onRemoveMainPhotoHandler = async () => {
    try {
      setSaving(true)
      await onUpdateMainPhoto(project?.id, null)
      setHref(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={"relative group"}>
      <Spin spinning={saving}>
        <Image
          loading={"lazy"}
          rootClassName={"size-full"}
          preview={{
            mask: null,
            visible: preview,
            src,
            onVisibleChange: (value) => !value && setPreview(value) // only for close
          }}
          src={src || "/placeholder.svg"}
          placeholder={<Skeleton.Node active className={"size-full"} />}
          className="h-40 object-cover"
        />
      </Spin>
      {photoEditable && (
        <Space
          className={`absolute hidden top-1 left-3 animate-in fade-in zoom-in-50 duration-300 group-hover:flex ${saving ? "pointer-events-none" : ""}`}
        >
          {src && (
            <>
              <Tooltip title={"Preview"}>
                <Button
                  disabled={saving}
                  onClick={() => setPreview(true)}
                  icon={<EyeFilled />}
                />
              </Tooltip>
              <Tooltip title={"Remove"}>
                <Button
                  loading={saving}
                  onClick={onRemoveMainPhotoHandler}
                  icon={<DeleteFilled />}
                />
              </Tooltip>
            </>
          )}

          <Upload
            customRequest={() => null}
            showUploadList={false}
            multiple={false}
            accept={ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
            beforeUpload={beforeUpload}
          >
            <Tooltip title={"Upload"}>
              <Button loading={saving} icon={<UploadOutlined />} />
            </Tooltip>
          </Upload>
        </Space>
      )}
    </div>
  )
}
